/**
 * CSV Format Handler - CSV export for spreadsheet compatibility
 *
 * This handler converts trip data into CSV format for analysis in spreadsheet
 * applications like Excel, Google Sheets, etc. Provides multiple CSV files
 * for different entity types and relationship data.
 *
 * Key Features:
 * - Separate CSV files for each entity type
 * - Relationship mapping files
 * - Configurable column selection
 * - Excel-compatible formatting
 * - Date and number formatting options
 * - Multi-file ZIP packaging
 */

class CsvFormatHandler {
  constructor() {
    this.formatVersion = '1.0.0';
    this.delimiter = ',';
    this.encoding = 'utf-8';
  }

  /**
   * Process data for CSV export
   * @param {Object} data - Aggregated trip data from all packages
   * @param {Object} options - Export options (delimiter, encoding, etc.)
   * @returns {Object} Processed export result with multiple CSV files
   */
  async process(data, options = {}) {
    try {
      // Configure CSV options
      this.delimiter = options.delimiter || ',';
      this.encoding = options.encoding || 'utf-8';

      // Generate CSV files for each entity type
      const csvFiles = await this.generateCsvFiles(data, options);

      // Create summary file
      const summaryFile = this.generateSummaryFile(data, csvFiles);

      // Package files (ZIP if multiple files)
      const packagedResult = await this.packageFiles([summaryFile, ...csvFiles], options);

      return {
        format: 'csv',
        version: this.formatVersion,
        files: packagedResult.files,
        packageType: packagedResult.type,
        data: packagedResult.data,
        size: packagedResult.size,
        filename: this.generateFilename(data.metadata, packagedResult.extension),
        mimeType: packagedResult.mimeType
      };

    } catch (error) {
      throw new Error(`CSV export failed: ${error.message}`);
    }
  }

  /**
   * Generate CSV files for each entity type
   * @param {Object} data - Trip data
   * @param {Object} options - Generation options
   * @returns {Array} Array of CSV file objects
   */
  async generateCsvFiles(data, options) {
    const csvFiles = [];

    // Define entity types and their CSV configurations
    const entityConfigs = {
      days: {
        filename: 'days.csv',
        columns: ['id', 'name', 'date', 'description', 'location', 'createdAt', 'updatedAt'],
        transforms: {
          date: this.formatDate,
          createdAt: this.formatDateTime,
          updatedAt: this.formatDateTime
        }
      },
      places: {
        filename: 'places.csv',
        columns: ['id', 'name', 'address', 'latitude', 'longitude', 'type', 'description'],
        transforms: {
          latitude: this.formatNumber,
          longitude: this.formatNumber
        }
      },
      accommodation: {
        filename: 'accommodation.csv',
        columns: ['id', 'name', 'type', 'address', 'checkIn', 'checkOut', 'guests', 'cost', 'currency'],
        transforms: {
          checkIn: this.formatDate,
          checkOut: this.formatDate,
          cost: this.formatCurrency
        }
      },
      travel: {
        filename: 'travel.csv',
        columns: ['id', 'type', 'origin', 'destination', 'departureTime', 'arrivalTime', 'cost', 'currency'],
        transforms: {
          departureTime: this.formatDateTime,
          arrivalTime: this.formatDateTime,
          cost: this.formatCurrency
        }
      },
      documents: {
        filename: 'documents.csv',
        columns: ['id', 'name', 'type', 'uploadDate', 'size', 'associatedEntities'],
        transforms: {
          uploadDate: this.formatDateTime,
          size: this.formatFileSize,
          associatedEntities: this.formatArray
        }
      },
      extras: {
        filename: 'extras.csv',
        columns: ['id', 'name', 'type', 'description', 'cost', 'currency', 'date'],
        transforms: {
          cost: this.formatCurrency,
          date: this.formatDate
        }
      }
    };

    // Generate CSV for each entity type
    for (const [entityType, config] of Object.entries(entityConfigs)) {
      const entities = data[entityType] || [];

      if (entities.length > 0) {
        const csvContent = this.generateEntityCsv(entities, config, options);
        csvFiles.push({
          filename: config.filename,
          content: csvContent,
          entityType,
          recordCount: entities.length
        });
      }
    }

    // Generate relationships CSV if relationships exist
    if (data.relationships && data.relationships.length > 0) {
      const relationshipsCsv = this.generateRelationshipsCsv(data.relationships, options);
      csvFiles.push({
        filename: 'relationships.csv',
        content: relationshipsCsv,
        entityType: 'relationships',
        recordCount: data.relationships.length
      });
    }

    return csvFiles;
  }

  /**
   * Generate CSV content for a specific entity type
   * @param {Array} entities - Array of entities
   * @param {Object} config - Entity CSV configuration
   * @param {Object} options - Generation options
   * @returns {string} CSV content
   */
  generateEntityCsv(entities, config, options) {
    const lines = [];

    // Add header row
    lines.push(this.escapeCsvRow(config.columns));

    // Add data rows
    entities.forEach(entity => {
      const row = config.columns.map(column => {
        let value = entity[column] || '';

        // Apply transformations if defined
        if (config.transforms && config.transforms[column]) {
          value = config.transforms[column](value);
        }

        return this.escapeCsvValue(value);
      });

      lines.push(row.join(this.delimiter));
    });

    return lines.join('\n');
  }

  /**
   * Generate relationships CSV
   * @param {Array} relationships - Array of relationship objects
   * @param {Object} options - Generation options
   * @returns {string} CSV content
   */
  generateRelationshipsCsv(relationships, options) {
    const lines = [];
    const columns = ['id', 'sourceType', 'sourceId', 'targetType', 'targetId', 'relationshipType', 'createdAt'];

    // Add header row
    lines.push(this.escapeCsvRow(columns));

    // Add relationship rows
    relationships.forEach(rel => {
      const row = columns.map(column => {
        let value = rel[column] || '';
        if (column === 'createdAt') {
          value = this.formatDateTime(value);
        }
        return this.escapeCsvValue(value);
      });

      lines.push(row.join(this.delimiter));
    });

    return lines.join('\n');
  }

  /**
   * Generate summary file with export metadata
   * @param {Object} data - Original trip data
   * @param {Array} csvFiles - Generated CSV files
   * @returns {Object} Summary file object
   */
  generateSummaryFile(data, csvFiles) {
    const summary = [
      'Trip Planner CSV Export Summary',
      '',
      `Export Date: ${new Date().toISOString()}`,
      `User ID: ${data.metadata.userId || 'N/A'}`,
      `Trip ID: ${data.metadata.tripId || 'All Trips'}`,
      `Export Scope: ${data.metadata.scope || 'Full'}`,
      '',
      'Files Included:',
      ...csvFiles.map(file => `- ${file.filename}: ${file.recordCount} records`),
      '',
      'Column Descriptions:',
      '- id: Unique identifier for each record',
      '- createdAt/updatedAt: ISO datetime stamps',
      '- Costs are in original currency (see currency column)',
      '- Coordinates are in decimal degrees',
      '- Dates are in YYYY-MM-DD format',
      '',
      'Notes:',
      '- Empty cells indicate no data was available',
      '- Relationships file shows connections between entities',
      '- For full data fidelity, use JSON export format'
    ];

    return {
      filename: 'README.txt',
      content: summary.join('\n'),
      entityType: 'summary',
      recordCount: 1
    };
  }

  /**
   * Package multiple files into appropriate format
   * @param {Array} files - Array of file objects
   * @param {Object} options - Packaging options
   * @returns {Object} Packaged result
   */
  async packageFiles(files, options) {
    if (files.length === 1) {
      // Single file - return as-is
      const file = files[0];
      return {
        type: 'single',
        files: files,
        data: file.content,
        size: this.calculateSize(file.content),
        extension: 'csv',
        mimeType: 'text/csv'
      };
    } else {
      // Multiple files - create ZIP package
      const zipData = await this.createZipPackage(files, options);
      return {
        type: 'zip',
        files: files,
        data: zipData.content,
        size: zipData.size,
        extension: 'zip',
        mimeType: 'application/zip'
      };
    }
  }

  /**
   * Create ZIP package for multiple CSV files
   * @param {Array} files - Array of file objects
   * @param {Object} options - ZIP options
   * @returns {Object} ZIP package data
   */
  async createZipPackage(files, options) {
    // TODO: Implement actual ZIP creation
    // For now, return mock ZIP data
    const mockZipContent = `ZIP Package containing ${files.length} files:\n` +
      files.map(f => `- ${f.filename} (${f.recordCount} records)`).join('\n');

    return {
      content: mockZipContent,
      size: this.calculateSize(mockZipContent)
    };
  }

  /**
   * Parse CSV data for import (basic CSV parsing)
   * @param {string} csvData - CSV data to parse
   * @param {Object} options - Parsing options
   * @returns {Object} Parsed data structure
   */
  async parse(csvData, options = {}) {
    try {
      const delimiter = options.delimiter || this.delimiter;
      const lines = csvData.trim().split('\n');

      if (lines.length < 2) {
        throw new Error('CSV must contain at least header and one data row');
      }

      // Parse header
      const headers = this.parseCsvRow(lines[0], delimiter);

      // Parse data rows
      const records = [];
      for (let i = 1; i < lines.length; i++) {
        const values = this.parseCsvRow(lines[i], delimiter);
        const record = {};

        headers.forEach((header, index) => {
          record[header] = values[index] || '';
        });

        records.push(record);
      }

      return {
        headers,
        records,
        recordCount: records.length,
        isValid: true
      };

    } catch (error) {
      throw new Error(`CSV parse failed: ${error.message}`);
    }
  }

  // Utility methods for CSV formatting

  /**
   * Escape CSV row (array of values)
   */
  escapeCsvRow(values) {
    return values.map(value => this.escapeCsvValue(value)).join(this.delimiter);
  }

  /**
   * Escape individual CSV value
   */
  escapeCsvValue(value) {
    if (value === null || value === undefined) {
      return '';
    }

    const stringValue = String(value);

    // Check if value needs quoting
    if (stringValue.includes(this.delimiter) ||
        stringValue.includes('"') ||
        stringValue.includes('\n') ||
        stringValue.includes('\r')) {

      // Escape quotes by doubling them and wrap in quotes
      return '"' + stringValue.replace(/"/g, '""') + '"';
    }

    return stringValue;
  }

  /**
   * Parse CSV row into array of values
   */
  parseCsvRow(row, delimiter) {
    const values = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < row.length; i++) {
      const char = row[i];

      if (char === '"') {
        if (inQuotes && row[i + 1] === '"') {
          current += '"';
          i++; // Skip next quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === delimiter && !inQuotes) {
        values.push(current);
        current = '';
      } else {
        current += char;
      }
    }

    values.push(current);
    return values;
  }

  // Data transformation methods

  formatDate(value) {
    if (!value) return '';
    try {
      return new Date(value).toISOString().split('T')[0];
    } catch {
      return value;
    }
  }

  formatDateTime(value) {
    if (!value) return '';
    try {
      return new Date(value).toISOString();
    } catch {
      return value;
    }
  }

  formatNumber(value) {
    if (value === null || value === undefined || value === '') return '';
    return Number(value).toString();
  }

  formatCurrency(value) {
    if (value === null || value === undefined || value === '') return '';
    return Number(value).toFixed(2);
  }

  formatFileSize(value) {
    if (!value) return '';
    const bytes = Number(value);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  formatArray(value) {
    if (!Array.isArray(value)) return value;
    return value.join('; ');
  }

  /**
   * Generate appropriate filename for export
   */
  generateFilename(metadata, extension) {
    const timestamp = new Date().toISOString().split('T')[0];
    const tripId = metadata.tripId ? `-${metadata.tripId}` : '';
    const scope = metadata.scope ? `-${metadata.scope}` : '';

    return `trip-planner-export${tripId}${scope}-${timestamp}.${extension}`;
  }

  /**
   * Calculate data size
   */
  calculateSize(data) {
    return new Blob([data]).size;
  }

  /**
   * Generate CSV content for itinerary data
   * Simple method for itinerary export compatibility
   *
   * @param {Array} itineraryData - Array of day objects with itinerary information
   * @returns {string} CSV content as string
   */
  generateItineraryCsv(itineraryData) {
    const lines = [];

    // Define columns for itinerary export (4 columns: Day, Date, Activities, Accommodation)
    const columns = [
      'Day',
      'Date',
      'Activities',
      'Accommodation'
    ];

    // Add header row
    lines.push(this.escapeCsvRow(columns));

    // Add data rows
    itineraryData.forEach(day => {
      const row = [
        day.dayNumber || '',
        day.date || '',
        Array.isArray(day.activities) ? day.activities.join('; ') : (day.activities || ''),
        day.accommodation || ''
      ].map(value => this.escapeCsvValue(value));

      lines.push(row.join(this.delimiter));
    });

    return lines.join('\n');
  }
}

// Export handler instance
const csvFormatHandler = new CsvFormatHandler();

export { csvFormatHandler, CsvFormatHandler };
export default csvFormatHandler;